/**
 * CSV formula-injection (a.k.a. CSV injection / DDE injection) guard.
 *
 * A spreadsheet that opens a CSV evaluates any cell it reads as a formula. A value like
 * `=HYPERLINK("http://evil","click")` or `=cmd|' /C calc'!A0` that a user typed into a form
 * then runs on the machine of whoever exports the data. OWASP's mitigation, used here: prefix
 * a risky text cell with a single quote so the spreadsheet shows it as text.
 *
 * Risky = the first character is one of `= + - @`, or a TAB / CR (those can be concatenated into
 * a following formula by some importers). Leading whitespace is skipped before the check because
 * several spreadsheets trim before they evaluate.
 *
 * Deliberately NOT risky (so exports of ordinary data are not littered with apostrophes):
 *   - a plain number such as `-5`, `+12`, `-0.25`, `1e-3` (cannot be anything but a number);
 *   - a lone `-` (the common "no value" placeholder; it cannot start a formula on its own).
 * Phone-like text such as `+1 (555) 010-0100` IS prefixed. That is the accepted OWASP trade-off.
 */

const TRIGGER_CHARS = new Set(["=", "+", "-", "@"]);

/**
 * Characters skipped before looking for a trigger: whitespace, C0 controls, DEL, NBSP, zero-width
 * spaces / joiners, word joiner and BOM. Built from String.fromCharCode so the invisible characters
 * stay visible in the source and survive any formatter.
 */
const NOISE_CODES = [0x00a0, 0x2060, 0xfeff] as const;
const LEADING_NOISE = new RegExp(
  `^[\\s\\x00-\\x1f\\x7f${NOISE_CODES.map((c) => String.fromCharCode(c)).join("")}${String.fromCharCode(0x200b)}-${String.fromCharCode(0x200d)}]*`
);
const PLAIN_NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;

/** True when a spreadsheet could evaluate `value` as a formula. */
export function isFormulaRisk(value: string): boolean {
  if (value.length === 0) return false;
  const first = value[0];
  if (first === "\t" || first === "\r") return true;

  const noise = LEADING_NOISE.exec(value);
  const offset = noise ? noise[0].length : 0;
  if (offset >= value.length) return false;
  if (!TRIGGER_CHARS.has(value[offset] as string)) return false;

  const body = value.slice(offset).trimEnd();
  if (body === "-") return false;
  if (PLAIN_NUMBER.test(body)) return false;
  return true;
}

/** Prefix a risky value with `'` so a spreadsheet treats it as text. Safe values are returned unchanged. */
export function neutraliseFormula(value: string): string {
  return isFormulaRisk(value) ? `'${value}` : value;
}
